/**
 * useProposalSummary Hook
 * Fetches and caches AI-generated proposal summary for an opportunity
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAPIClient } from '@tracekit/core';

export interface ProposalSummaryRequest {
  include_past_proposals?: boolean;
  include_capabilities?: boolean;
  include_sops?: boolean;
  max_length?: number;
}

export interface ProposalSummaryResponse {
  summary: string;
  key_points: string[];
  match_confidence: number;
  relevant_capabilities: string[];
  citations: Array<{
    source?: string;
    page?: number;
    content?: string;
    confidence?: number;
  }>;
  summary_source: string;
}

/**
 * Hook to fetch proposal AI summary for an opportunity
 * 
 * @param opportunityId - UUID of the opportunity entity
 * @param projectId - Project ID
 * @param request - Optional request parameters
 * @param enabled - Whether the query should be enabled (default: true)
 * 
 * @returns Query result with summary data, loading state, and error handling
 */
export function useProposalSummary(
  opportunityId: string | null | undefined,
  projectId: string | null | undefined,
  request: ProposalSummaryRequest = {},
  enabled: boolean = true
) {
  const api = getAPIClient();

  // Create a stable query key that doesn't change on every render
  const queryKey = React.useMemo(() => [
    'proposal-summary',
    opportunityId,
    projectId,
    request.include_past_proposals,
    request.include_capabilities,
    request.include_sops,
    request.max_length,
  ], [opportunityId, projectId, request.include_past_proposals, request.include_capabilities, request.include_sops, request.max_length]);

  return useQuery<ProposalSummaryResponse>({
    queryKey,
    queryFn: async () => {
      if (!opportunityId || !projectId) {
        throw new Error('Opportunity ID and Project ID are required');
      }

      console.log('[useProposalSummary] Fetching summary for opportunity:', opportunityId);

      try {
        const response = await api.post<ProposalSummaryResponse>(
          `/api/opportunities/${opportunityId}/summary`,
          {
            include_past_proposals: request.include_past_proposals ?? true,
            include_capabilities: request.include_capabilities ?? true,
            include_sops: request.include_sops ?? true,
            max_length: request.max_length ?? 500,
          },
          {
            params: {
              project_id: projectId,
            },
          }
        );

        // API client returns response.data directly, so response IS the ProposalSummaryResponse
        const data = response;

        // Validate response data
        if (!data || typeof data !== 'object') {
          console.error('[useProposalSummary] Response data is invalid:', data);
          throw new Error('Invalid response: data is undefined or invalid');
        }
        const processedData = {
          summary: data.summary || 'No summary available.',
          key_points: Array.isArray(data.key_points) ? data.key_points : [],
          match_confidence: typeof data.match_confidence === 'number' ? data.match_confidence : 0.0,
          relevant_capabilities: Array.isArray(data.relevant_capabilities) ? data.relevant_capabilities : [],
          citations: Array.isArray(data.citations) ? data.citations : [],
          summary_source: data.summary_source || 'unknown',
        };

        console.log('[useProposalSummary] Successfully processed summary:', {
          summaryLength: processedData.summary.length,
          keyPointsCount: processedData.key_points.length,
          capabilitiesCount: processedData.relevant_capabilities.length,
          matchConfidence: processedData.match_confidence,
        });

        return processedData;
      } catch (error) {
        console.error('[useProposalSummary] Error fetching summary:', error);
        throw error;
      }
    },
    enabled: enabled && !!opportunityId && !!projectId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 1, // Reduce retries to prevent multiple calls
    retryDelay: 1000,
  });
}

